import { useEffect, useState } from 'react';
import { ConnectStoreForm } from './ConnectStoreForm';
import { authFetch, API_URL } from '../hooks/useAuth';

const CONNECT_NEW = '__connect_new__';

// Store dropdown for billing: lists the caller's OAuth-connected stores, preselects the billing store,
// offers "Connect another store" (disabled at the plan's store limit), and shows per-store plan state.
export const BillingStoreSelector = ({ open, selectedShop, onSelect, onStoresLoaded }) => {
  const [data, setData] = useState(null);
  const [showConnect, setShowConnect] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    authFetch(`${API_URL}/api/billing/stores`).then(r => r.json()).then(d => {
      if (cancelled) return;
      setData(d);
      onStoresLoaded?.(d);
      if (!d.stores?.length) setShowConnect(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, onStoresLoaded]);

  if (!open) return null;
  if (!data) return <div data-testid="billing-store" style={{fontSize:'11px',color:'var(--text-muted)',textAlign:'center'}}>Loading connected stores…</div>;

  const atLimit = data.installs_limit !== -1 && data.installs_used >= data.installs_limit;
  const current = data.stores.find(s => s.shop_domain === selectedShop);

  const handleChange = async (e) => {
    const v = e.target.value;
    if (v === CONNECT_NEW) { setShowConnect(true); return; }
    setShowConnect(false);
    onSelect(v);
    const r = await authFetch(`${API_URL}/api/billing/store`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shop: v }) });
    if (r.ok) { const d = await r.json(); setData(d); onStoresLoaded?.(d); }
  };

  return (
    <div data-testid="billing-store-selector" style={{display:'flex',flexDirection:'column',gap:'8px'}}>
      {data.stores.length > 0 && (
        <label style={{display:'flex',flexDirection:'column',gap:'4px',fontSize:'11px',color:'var(--text-muted)'}}>
          Billing store
          <select className="beige-input" value={showConnect ? CONNECT_NEW : (selectedShop || '')} onChange={handleChange} data-testid="billing-store-select">
            {data.stores.map(s => (
              <option key={s.shop_domain} value={s.shop_domain}>{s.shop_domain} — {s.plan}{s.interval ? ` (${s.interval})` : ''}</option>
            ))}
            <option value={CONNECT_NEW} disabled={atLimit}>{atLimit ? `Connect another store (limit ${data.installs_limit} reached)` : 'Connect another store…'}</option>
          </select>
        </label>
      )}
      {current && !showConnect && (
        <div data-testid="billing-store" style={{fontSize:'11px',color:'var(--text-muted)',textAlign:'center'}}>
          Plan for <strong>{current.shop_domain}</strong>: {current.plan}{current.runs_limit === -1 ? ' · unlimited runs' : ` · ${current.runs_used}/${current.runs_limit} runs`}
          {' · '}<span data-testid="billing-stores-count">Billing stores: {data.installs_used}/{data.installs_limit === -1 ? '∞' : data.installs_limit}</span>
          <div data-testid="billing-stores-help" style={{fontSize:'10px',marginTop:'2px',opacity:0.85}}>Stores you can bill from. Managed stores are counted separately.</div>
        </div>
      )}
      {atLimit && data.stores.length > 0 && (
        <div data-testid="store-limit-notice" style={{fontSize:'11px',color:'var(--accent-red)',textAlign:'center'}}>
          Your plan allows {data.installs_limit} connected store{data.installs_limit === 1 ? '' : 's'}. Upgrade the plan below to connect more.
        </div>
      )}
      {showConnect && !atLimit && <ConnectStoreForm defaultShop="" />}
    </div>
  );
};
